/**
 * Meridian theme tokens — canonical TypeScript mirror of the
 * design-system palette.
 *
 * The runtime application of these tokens is via CSS custom
 * properties declared in ``src/styles/globals.css``: ``:root``
 * carries the light palette and ``[data-theme="dark"]`` carries
 * the dark palette. Tailwind reads these through ``hsl(var(--…))``
 * indirection per ``tailwind.config.js``.
 *
 * This file exists for three reasons:
 *
 * 1. **Single source of truth.** The hex values mirrored to CSS
 *    live here so a reader can navigate from any consumer to the
 *    storyboard design-system in one hop without diffing two
 *    files.
 * 2. **Cross-stream contract.** Streams A (map), C (multi-pane),
 *    D (onboarding) import :data:`THEME_TOKENS` for any palette
 *    decision they cannot resolve through Tailwind directly
 *    (e.g., MapLibre map-tile JSON style overrides — Stream A
 *    consumes ``THEME_TOKENS.dark.surface[0]`` as the map canvas
 *    background for REQ-UI-045 when the dark tile style lands).
 * 3. **Theme-resolver type discipline.** The :type:`Theme` and
 *    :type:`ResolvedTheme` discriminator types in this file are
 *    used by every consumer of :func:`usePreferences` to
 *    distinguish the user's *choice* (one of three) from the
 *    *resolved* theme that actually applies to the DOM (one of
 *    two).
 *
 * Cross-references:
 *   - REQ-UI-040 (light/dark/system theme).
 *   - REQ-UI-046 (status palette retains semantic meaning + WCAG AA).
 *   - ADR-022 §Feature 2 (theme + paired palettes).
 *   - ``docs/engineering/detail/storyboards/shared/design-system.md``
 *     — canonical hex values; this file mirrors that document.
 */

/**
 * The user's theme *choice* — what the preferences UI offers.
 *
 * Persisted in the BFF's ``auth.user_preferences.preferences.theme``
 * column (REQ-AUTH-053) and in the localStorage cache for
 * pre-bootstrap application (REQ-UI-044).
 */
export type Theme = "light" | "dark" | "system";

/**
 * The *resolved* theme applied to the DOM. ``"system"`` is collapsed
 * to either ``"light"`` or ``"dark"`` via the OS/browser
 * ``prefers-color-scheme`` media query at runtime (REQ-UI-041).
 */
export type ResolvedTheme = "light" | "dark";

/**
 * Palette shape — both light and dark export this same surface.
 *
 * The keys here are the conceptual buckets from
 * ``design-system.md``; the hex values are pasted verbatim. When
 * a token isn't represented in Tailwind directly (e.g., the
 * map-canvas gradient, the dock-indicator label background), a
 * consumer reaches into :data:`THEME_TOKENS` for the value.
 */
export interface ThemePalette {
  readonly surface: {
    readonly 0: string;
    readonly 1: string;
    readonly 2: string;
    readonly 3: string;
  };
  readonly border: {
    readonly subtle: string;
    readonly default: string;
    readonly strong: string;
  };
  readonly text: {
    readonly primary: string;
    readonly secondary: string;
    readonly tertiary: string;
    readonly inverse: string;
  };
  readonly action: {
    readonly base: string;
    readonly hover: string;
    readonly subtle: string;
  };
  readonly status: {
    readonly healthy: string;
    readonly degraded: string;
    readonly unhealthy: string;
    readonly unknown: string;
    readonly stale: string;
  };
  readonly tint: {
    readonly healthy: string;
    readonly degraded: string;
    readonly unhealthy: string;
  };
  readonly topbar: {
    readonly bg: string;
    readonly border: string;
  };
  readonly map: {
    readonly bgFrom: string;
    readonly bgTo: string;
    readonly tileLine: string;
  };
}

/**
 * Light palette — verbatim from ``design-system.md`` §"Color"
 * 2026-05-26 amendment (Appaloosa-aligned aesthetic refresh).
 *
 * Light is the user-selected override; dark is the default for
 * production per CLAUDE.md §6.
 */
const LIGHT: ThemePalette = {
  surface: {
    0: "#f5f6f8",
    1: "#ffffff",
    2: "#ebedf2",
    3: "#dfe1e8",
  },
  border: {
    subtle: "#ebedf2",
    default: "#c8ccd8",
    strong: "#9199ad",
  },
  text: {
    primary: "#1a1d2b",
    secondary: "#5c6278",
    tertiary: "#9199ad",
    inverse: "#ffffff",
  },
  action: {
    base: "#0d61a5",
    hover: "#0b538e",
    subtle: "#d4eaf7",
  },
  status: {
    healthy: "#1a9960",
    degraded: "#b8711a",
    unhealthy: "#c93b33",
    unknown: "#9199ad",
    stale: "#c8ccd8",
  },
  tint: {
    healthy: "#dcf9ec",
    degraded: "#faecdb",
    unhealthy: "#faebeb",
  },
  topbar: {
    bg: "#ffffff",
    border: "#c8ccd8",
  },
  map: {
    bgFrom: "#ebedf2",
    bgTo: "#c8ccd8",
    tileLine: "#9199ad",
  },
};

/**
 * Dark palette — verbatim from ``design-system.md`` §"Color"
 * 2026-05-26 amendment (Appaloosa-aligned aesthetic refresh).
 *
 * Dark is the default production palette; ``:root`` in
 * :file:`src/styles/globals.css` carries these values without
 * any ``data-theme`` qualifier.
 */
const DARK: ThemePalette = {
  surface: {
    0: "#0f1117",
    1: "#181c27",
    2: "#1f2437",
    3: "#252a3a",
  },
  border: {
    subtle: "#1f2437",
    default: "#2e3450",
    strong: "#4f566b",
  },
  text: {
    primary: "#e8eaf0",
    secondary: "#8b92aa",
    tertiary: "#4f566b",
    inverse: "#0f1117",
  },
  action: {
    base: "#f5a623",
    hover: "#f7b655",
    subtle: "#7a5010",
  },
  status: {
    healthy: "#3ecf8e",
    degraded: "#f5a623",
    unhealthy: "#e5534b",
    unknown: "#8b92aa",
    stale: "#4f566b",
  },
  tint: {
    healthy: "#145236",
    degraded: "#613e05",
    unhealthy: "#62120e",
  },
  topbar: {
    bg: "#181c27",
    border: "#2e3450",
  },
  map: {
    bgFrom: "#181c27",
    bgTo: "#0f1117",
    tileLine: "#4f566b",
  },
};

/**
 * Paired-palette container. Consumers prefer reading values
 * through Tailwind (``bg-background``, etc.); reach into this
 * map only when a token can't be expressed as a Tailwind class
 * (canvas gradient stops, MapLibre style overrides per REQ-UI-045).
 */
export const THEME_TOKENS: { readonly light: ThemePalette; readonly dark: ThemePalette } = {
  light: LIGHT,
  dark: DARK,
};

/**
 * The three theme options offered by the preferences UI, in the
 * display order specified by Flow P (light, dark, system).
 */
export const THEME_OPTIONS: readonly Theme[] = ["light", "dark", "system"] as const;

/** Type guard — narrows ``unknown`` to :type:`Theme`. */
export function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark" || value === "system";
}
